import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import auth from '../../../configs/auth';
import { persistor } from '../../../redux/store';
import {
  getDocumentConfigurationRequest,
  getDocumentsRequest,
  uploadJewellerDocumentRequest,
  clearUploadJewellerDocument,
  updateJewellerDocumentRequest,
} from '../../../redux/JewellerDocument/jewellerDocumentSlice';

const EXTENSION_ALIASES = {
  pdf: ['pdf'],
  png: ['png'],
  jpeg: ['jpeg', 'jpg'],
  jpg: ['jpg', 'jpeg'],
};

const MIME_ALIASES = {
  pdf: ['application/pdf'],
  png: ['image/png'],
  jpeg: ['image/jpeg', 'image/jpg'],
  jpg: ['image/jpeg', 'image/jpg'],
};

const fileExtension = (file) => {
  const name = String(file?.name || '');
  const parts = name.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

const normalizedTypes = (fileTypes) =>
  (Array.isArray(fileTypes) ? fileTypes : [])
    .map((type) => String(type || '').trim().toLowerCase())
    .filter(Boolean);

const fileAllowed = (file, fileTypes) => {
  const extension = fileExtension(file);
  const mime = String(file?.type || '').toLowerCase();
  return normalizedTypes(fileTypes).some((type) => {
    const extensions = EXTENSION_ALIASES[type] || [type];
    const mimes = MIME_ALIASES[type] || [];
    return extensions.includes(extension) || mimes.includes(mime);
  });
};

const acceptValue = (fileTypes) => {
  const values = new Set();
  normalizedTypes(fileTypes).forEach((type) => {
    (EXTENSION_ALIASES[type] || [type]).forEach((extension) =>
      values.add(`.${extension}`),
    );
    (MIME_ALIASES[type] || []).forEach((mime) => values.add(mime));
  });
  return Array.from(values).join(',');
};

const formatBytes = (bytes) => {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const fileLabel = (value) => {
  const clean = String(value || '').split('?')[0];
  try {
    return decodeURIComponent(clean.split('/').pop() || 'file');
  } catch {
    return clean.split('/').pop() || 'file';
  }
};

const mediaKind = (name, type = '') => {
  const value = `${type} ${name}`.toLowerCase();
  if (value.includes('pdf') || /\.pdf($|\s)/.test(value)) return 'pdf';
  if (
    value.includes('image') ||
    /\.(png|jpe?g|webp|gif)($|\s)/.test(value)
  ) {
    return 'image';
  }
  return 'file';
};

const PreviewFrame = ({ kind, url, name, onClosed }) => {
  const [open, setOpen] = useState(false);
  const canPreview = Boolean(url) && (kind === 'image' || kind === 'pdf');
  const closePreview = () => {
    setOpen(false);
    if (onClosed) onClosed();
  };

  return (
    <>
      <Box
        sx={{ width: 84, flexShrink: 0, cursor: canPreview ? 'pointer' : 'default' }}
        role={canPreview ? 'button' : undefined}
        onClick={() => {
          if (canPreview) setOpen(true);
        }}
      >
        <Box
          title={name}
          sx={{
            width: 84,
            height: 104,
            borderRadius: '10px',
            overflow: 'hidden',
            border: '1px solid #e0e3e7',
            bgcolor: '#f8f9fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': canPreview
              ? { borderColor: '#1a73e8', boxShadow: '0 0 0 2px #e8f0fe' }
              : undefined,
          }}
        >
          {kind === 'image' && url ? (
            <Box
              component="img"
              src={url}
              alt={name}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : kind === 'pdf' && url ? (
            <Box
              component="iframe"
              title={name}
              src={`${url}#toolbar=0&navpanes=0&scrollbar=0`}
              sx={{
                width: 168,
                height: 208,
                border: 0,
                transform: 'scale(0.5)',
                transformOrigin: 'top left',
                pointerEvents: 'none',
                bgcolor: '#fff',
              }}
            />
          ) : (
            <PictureAsPdfOutlinedIcon
              sx={{ color: kind === 'pdf' ? '#d93025' : '#5f6368', fontSize: 28 }}
            />
          )}
        </Box>
        <Typography
          noWrap
          sx={{ fontSize: 11, color: '#5f6368', mt: 0.5, maxWidth: 84 }}
        >
          {name}
        </Typography>
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            bgcolor: '#202124',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 2,
            py: 1.25,
          }}
        >
          <Typography noWrap sx={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>
            {name}
          </Typography>
          <IconButton
            aria-label="Close preview"
            onClick={closePreview}
            sx={{ color: '#fff' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            px: 2,
            pb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { xs: 320, md: 520 },
          }}
        >
          {kind === 'image' ? (
            <Box
              component="img"
              src={url}
              alt={name}
              sx={{
                maxWidth: '100%',
                maxHeight: '78vh',
                objectFit: 'contain',
                borderRadius: '8px',
                bgcolor: '#fff',
              }}
            />
          ) : (
            <Box
              component="iframe"
              title={name}
              src={url}
              sx={{
                width: '100%',
                height: '78vh',
                border: 0,
                borderRadius: '8px',
                bgcolor: '#fff',
              }}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
};

const LocalPreview = ({ file }) => {
  const kind = mediaKind(file?.name, file?.type);
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (!file || kind === 'file') return undefined;
    const nextUrl = URL.createObjectURL(file);
    setUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [file, kind]);

  return <PreviewFrame kind={kind} url={url} name={file?.name || 'file'} />;
};

const RemotePreview = ({ path, onClosed }) => {
  const name = fileLabel(path);
  return <PreviewFrame kind={mediaKind(name)} url={path} name={name} onClosed={onClosed} />;
};

const mapConfiguration = (files) =>
  (Array.isArray(files) ? files : [])
    .map((item) => ({
      fileName: String(item?.fileName || '').trim().toUpperCase(),
      fileCount: Math.max(1, Number(item?.fileCount) || 1),
      fileType: Array.isArray(item?.fileType) ? item.fileType : [],
    }))
    .filter((item) => item.fileName);

const UploadJewellerDocument = ({ embedded = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const routeJewellerId = String(location.state?.jewellerId || '').trim();
  const routeJewellerName = String(location.state?.jewellerName || '').trim();
  const configuration = useSelector(
    (state) => state.jewellerDocument?.configuration,
  );
  const configurationLoaded = useSelector(
    (state) => state.jewellerDocument?.configurationLoaded,
  );
  const configurationLoading = useSelector(
    (state) => state.jewellerDocument?.configurationLoading,
  );
  const isLoading = !configurationLoaded || configurationLoading;
  const uploadingIndex = useSelector(
    (state) => state.jewellerDocument?.uploadingIndex,
  );
  const uploadResult = useSelector(
    (state) => state.jewellerDocument?.uploadResult,
  );
  const uploadError = useSelector(
    (state) => state.jewellerDocument?.uploadError,
  );
  const savedJewellerDocuments = useSelector(
    (state) => state.jewellerDocument?.documents,
  );
  const savedJewellerId = useSelector(
    (state) => state.jewellerDocument?.documentsJewellerId,
  );
  const documents = useMemo(
    () => mapConfiguration(configuration),
    [configuration],
  );
  const [filesByIndex, setFilesByIndex] = useState({});
  const [errorByIndex, setErrorByIndex] = useState({});
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [uploadedByIndex, setUploadedByIndex] = useState({});
  const [forwardOpen, setForwardOpen] = useState(false);
  const [showResubmit, setShowResubmit] = useState(false);
  const handledUploadRef = useRef(null);
  const handledErrorRef = useRef(null);
  const promptedForRef = useRef(null);
  const savedUserName = useMemo(() => {
    if (routeJewellerName) return routeJewellerName;
    try {
      const raw =
        localStorage.getItem('user') ||
        localStorage.getItem('adminUserData') ||
        '{}';
      const savedUser = JSON.parse(raw);
      const combinedName = [savedUser?.firstName, savedUser?.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();
      return String(
        savedUser?.name || combinedName || savedUser?.jewellerName || '',
      ).trim();
    } catch {
      return '';
    }
  }, [routeJewellerName]);

  const filledExistingRef = useRef(false);

  useEffect(() => {
    dispatch(getDocumentConfigurationRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!routeJewellerId) return;
    filledExistingRef.current = false;
    dispatch(getDocumentsRequest({ jewellerId: routeJewellerId }));
  }, [routeJewellerId, dispatch]);

  const existingRecord =
    routeJewellerId && savedJewellerId === routeJewellerId
      ? (savedJewellerDocuments || []).find((item) => item?.file?.length) || null
      : null;

  useEffect(() => {
    if (!routeJewellerId || filledExistingRef.current || !existingRecord || !documents.length) {
      return;
    }
    const savedFiles = Array.isArray(existingRecord.file) ? existingRecord.file : [];
    const nextUploaded = {};
    documents.forEach((documentConfig, index) => {
      if ((filesByIndex[index] || []).length) return;
      const savedGroup = savedFiles.find(
        (item) =>
          String(item?.fileName || '').toUpperCase() ===
          String(documentConfig.fileName || '').toUpperCase(),
      );
      if (savedGroup) nextUploaded[index] = savedGroup;
    });
    filledExistingRef.current = true;
    setUploadedByIndex(nextUploaded);
  }, [routeJewellerId, existingRecord, documents, filesByIndex]);

  useEffect(() => {
    if (!uploadResult || handledUploadRef.current === uploadResult) return;
    handledUploadRef.current = uploadResult;
    const savedFiles = uploadResult.data?.file;
    if (!Array.isArray(savedFiles)) return;
    const nextUploaded = {};
    documents.forEach((documentConfig, index) => {
      const savedGroup = savedFiles.find(
        (item) =>
          String(item?.fileName || '').toUpperCase() ===
          String(documentConfig.fileName || '').toUpperCase(),
      );
      if (savedGroup) nextUploaded[index] = savedGroup;
    });
    setFilesByIndex({});
    setErrorByIndex({});
    setUploadedByIndex(nextUploaded);
    if (routeJewellerId) filledExistingRef.current = true;
  }, [uploadResult, documents, routeJewellerId]);

  useEffect(() => {
    if (!uploadError || handledErrorRef.current === uploadError) return;
    handledErrorRef.current = uploadError;
    setErrorByIndex((prev) => ({
      ...prev,
      [uploadError.cardIndex]: uploadError.message,
    }));
  }, [uploadError]);

  const setCardError = (index, message) => {
    setErrorByIndex((prev) => ({ ...prev, [index]: message || '' }));
  };

  const addFiles = (index, documentConfig, fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;

    const maxCount = documentConfig.fileCount;
    const current = filesByIndex[index] || [];
    const invalid = incoming.filter(
      (file) => !fileAllowed(file, documentConfig.fileType),
    );
    const valid = incoming.filter((file) =>
      fileAllowed(file, documentConfig.fileType),
    );
    const allowedLabel = (documentConfig.fileType || []).join(', ') || 'none';

    if (!documentConfig.fileType?.length) {
      setCardError(
        index,
        `${documentConfig.fileName} has no allowed file types`,
      );
      return;
    }

    if (invalid.length && !valid.length) {
      setCardError(
        index,
        `${invalid.map((file) => file.name).join(', ')} is not allowed. Use ${allowedLabel}.`,
      );
      return;
    }

    let nextFiles = current;
    let message = '';

    if (maxCount === 1) {
      nextFiles = valid.slice(0, 1);
      if (incoming.length > 1 || invalid.length) {
        message = `${documentConfig.fileName} allows 1 file of type ${allowedLabel}.`;
      }
    } else {
      const room = maxCount - current.length;
      if (room <= 0) {
        setCardError(
          index,
          `${documentConfig.fileName} allows a maximum of ${maxCount} files.`,
        );
        return;
      }
      const accepted = valid.slice(0, room);
      nextFiles = [...current, ...accepted];
      if (accepted.length < valid.length || invalid.length) {
        message = `${documentConfig.fileName} allows up to ${maxCount} files. Accepted types: ${allowedLabel}.`;
      }
    }

    setFilesByIndex((prev) => ({ ...prev, [index]: nextFiles }));
    setUploadedByIndex((prev) => ({ ...prev, [index]: null }));
    setCardError(index, message);
  };

  const removeFile = (index, fileIndex) => {
    setFilesByIndex((prev) => ({
      ...prev,
      [index]: (prev[index] || []).filter((_, itemIndex) => itemIndex !== fileIndex),
    }));
    setCardError(index, '');
  };

  const uploadDocument = async (index) => {
    let jewellerId = routeJewellerId || '';
    if (!jewellerId) {
      jewellerId = sessionStorage.getItem(auth.selectedJeweller) || '';
    }
    if (!jewellerId) {
      try {
        const user = JSON.parse(localStorage.getItem('adminUserData') || '{}');
        jewellerId =
          typeof user?.jeweller === 'string'
            ? user.jeweller
            : user?.jeweller?._id || '';
      } catch {
        jewellerId = '';
      }
    }
    if (!jewellerId) {
      toast.error('Jeweller id is required');
      return;
    }

    const changedDocuments = [];
    for (let itemIndex = 0; itemIndex < documents.length; itemIndex += 1) {
      const itemConfig = documents[itemIndex];
      const itemFiles = filesByIndex[itemIndex] || [];
      if (!itemFiles.length) continue;
      if (itemFiles.length > itemConfig.fileCount) {
        const message = `${itemConfig.fileName} allows a maximum of ${itemConfig.fileCount} files.`;
        setCardError(itemIndex, message);
        toast.error(message);
        return;
      }
      const invalid = itemFiles.filter(
        (file) => !fileAllowed(file, itemConfig.fileType),
      );
      if (invalid.length) {
        const message = `${itemConfig.fileName} only accepts ${(itemConfig.fileType || []).join(', ')}.`;
        setCardError(itemIndex, message);
        toast.error(message);
        return;
      }
      changedDocuments.push({
        fileName: itemConfig.fileName,
        files: itemFiles,
      });
    }

    if (existingRecord) {
      if (!changedDocuments.length) {
        toast.error('Change a document to update');
        return;
      }
      dispatch(
        updateJewellerDocumentRequest({
          jewellerId,
          documents: changedDocuments,
          cardIndex: -1,
        }),
      );
      return;
    }

    const files = filesByIndex[index] || [];
    const missing = documents
      .filter((_, itemIndex) => !(filesByIndex[itemIndex] || []).length)
      .map((item) => item.fileName);
    if (missing.length) {
      const message = `Upload all required documents: ${missing.join(', ')}`;
      if (!files.length) setCardError(index, message);
      toast.error(message);
      return;
    }

    dispatch(
      uploadJewellerDocumentRequest({
        jewellerId,
        documents: changedDocuments,
        cardIndex: -1,
      }),
    );
  };

  const uploadedCount = documents.filter((_, index) => uploadedByIndex[index]).length;
  const submissionComplete =
    documents.length > 0 && uploadedCount === documents.length;

  useEffect(() => {
    if (routeJewellerId) return;
    if (!submissionComplete || !uploadResult) return;
    if (promptedForRef.current === uploadResult) return;
    promptedForRef.current = uploadResult;
    setForwardOpen(true);
  }, [submissionComplete, uploadResult, routeJewellerId]);

  const stayOnPage = () => {
    setForwardOpen(false);
    setShowResubmit(true);
  };

  const goToDashboard = () => {
    localStorage.setItem('hasjewellerDocument', 'true');
    window.location.href = '/dashboard';
  };

  const logoutAfterUpload = async () => {
    localStorage.clear();
    sessionStorage.clear();
    dispatch({ type: 'RESET_APP' });
    await persistor.purge();
    window.location.replace('/login');
  };

  const closeUploadPage = () => {
    handledUploadRef.current = uploadResult;
    setFilesByIndex({});
    setUploadedByIndex({});
    setErrorByIndex({});
    setForwardOpen(false);
    setShowResubmit(false);
    dispatch(clearUploadJewellerDocument());
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/CreateJwellerList');
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: embedded ? 'auto' : '100%',
        bgcolor: '#f3f5f8',
        mt: embedded ? 0 : { xs: 8, md: 8 },
        px: embedded ? 0 : { xs: 2, sm: 3 },
        py: embedded ? 0 : { xs: 3, md: 4 },
        fontFamily: 'Roboto, Arial, sans-serif',
      }}
    >
      <Box sx={{ maxWidth: 960, mx: 'auto' }}>
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '18px',
            overflow: 'hidden',
            border: '1px solid #e6e8ec',
            boxShadow: '0 8px 24px rgba(32, 33, 36, 0.08)',
          }}
        >
          <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Typography
                sx={{
                  fontSize: 22,
                  color: '#202124',
                  fontWeight: 700,
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                {t('jewellerDocument.title')}
              </Typography>
              <IconButton
                aria-label="Close"
                onClick={closeUploadPage}
                sx={{ position: 'absolute', right: 0, color: '#5f6368' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <TextField
              label="Name"
              value={savedUserName}
              fullWidth
              margin="normal"
              InputProps={{ readOnly: true }}
              sx={{
                mt: 1.5,
                '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#f8f9fa' },
                '& .MuiInputBase-input': { fontWeight: 700, color: '#202124' },
              }}
            />
          </Box>

          {!isLoading && documents.length > 0 && (
            <Box
              sx={{
                mx: 2.5,
                mb: 2,
                py: 1.1,
                textAlign: 'center',
                borderRadius: '6px',
                bgcolor: submissionComplete ? '#188038' : '#f5a623',
                color: '#fff',
                fontWeight: 800,
                letterSpacing: 0.6,
                fontSize: 14,
              }}
            >
              {submissionComplete ? 'SUBMISSION COMPLETE' : 'SUBMISSION INCOMPLETE'}
            </Box>
          )}

          {submissionComplete && showResubmit && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mx: 2.5, mb: 2 }}>
              <Button
                variant="contained"
                onClick={() => setForwardOpen(true)}
                sx={{
                  minWidth: 160,
                  height: 40,
                  borderRadius: '6px',
                  bgcolor: '#188038',
                  color: '#fff',
                  textTransform: 'uppercase',
                  fontSize: 13,
                  fontWeight: 800,
                  letterSpacing: 0.6,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#137333', boxShadow: 'none' },
                }}
              >
                {t('jewellerDocument.resubmit')}
              </Button>
            </Box>
          )}

        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 6 }}>
            <CircularProgress size={22} sx={{ color: '#1a73e8' }} />
            <Typography sx={{ color: '#5f6368' }}>
              {t('jewellerDocument.loading')}
            </Typography>
          </Box>
        )}

        {!isLoading && documents.length === 0 && (
          <Box
            sx={{
              mt: 4,
              minHeight: 180,
              maxWidth: 420,
              borderRadius: '16px',
              bgcolor: '#202124',
              color: '#fff',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              px: 3,
            }}
          >
            <CloudUploadOutlinedIcon sx={{ fontSize: 48, color: '#e8eaed' }} />
            <Typography sx={{ fontSize: 22, mt: 2 }}>
              {t('jewellerDocument.emptyTitle')}
            </Typography>
            <Typography sx={{ color: '#9aa0a6', mt: 1, maxWidth: 420 }}>
              {t('jewellerDocument.empty')}
            </Typography>
          </Box>
        )}

          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {documents.map((documentConfig, index) => {
            const selectedFiles = filesByIndex[index] || [];
            const isUploading = typeof uploadingIndex === 'number';
            const uploaded = uploadedByIndex[index];
            const isFull = selectedFiles.length >= documentConfig.fileCount;

            return (
              <Box
                key={`${documentConfig.fileName}-${index}`}
                sx={{ borderTop: '1px solid #eceff3' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 5,
                      alignSelf: 'stretch',
                      borderRadius: '4px',
                      bgcolor: uploaded ? '#188038' : '#1a73e8',
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 14, color: '#202124', letterSpacing: 0.3 }}>
                      {documentConfig.fileName}
                    </Typography>
                    <Typography sx={{ color: '#5f6368', fontSize: 12, mt: 0.25 }}>
                      {selectedFiles.length}/{documentConfig.fileCount}
                      {(documentConfig.fileType || []).length
                        ? ` · ${(documentConfig.fileType || []).join(', ')}`
                        : ''}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  component="label"
                  htmlFor={`jeweller-doc-input-${index}`}
                  onDragOver={(event) => {
                    event.preventDefault();
                    if (!isFull && !isUploading) setDragOverIndex(index);
                  }}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setDragOverIndex(null);
                    if (!isUploading) {
                      addFiles(index, documentConfig, event.dataTransfer.files);
                    }
                  }}
                  sx={{
                    mx: 2,
                    mb: 1.5,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: '8px',
                    border: '1px dashed',
                    borderColor: dragOverIndex === index ? '#1a73e8' : '#dadce0',
                    bgcolor: dragOverIndex === index ? '#e8f0fe' : '#f8f9fa',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: isUploading || isFull ? 'not-allowed' : 'pointer',
                    opacity: isUploading ? 0.7 : 1,
                  }}
                >
                  <CloudUploadOutlinedIcon sx={{ color: '#1a73e8', fontSize: 20 }} />
                  <Typography sx={{ fontSize: 13, color: '#3c4043' }}>
                    {isFull
                      ? t('jewellerDocument.limitReached')
                      : t('jewellerDocument.dropTitle')}
                  </Typography>
                  <input
                    id={`jeweller-doc-input-${index}`}
                    type="file"
                    hidden
                    multiple={documentConfig.fileCount > 1}
                    accept={acceptValue(documentConfig.fileType)}
                    disabled={isUploading || isFull}
                    onChange={(event) => {
                      addFiles(index, documentConfig, event.target.files);
                      event.target.value = '';
                    }}
                  />
                </Box>

                <Box sx={{ px: 2, pb: selectedFiles.length || uploaded || errorByIndex[index] ? 1.5 : 0 }}>
                  {!!errorByIndex[index] && (
                    <Typography sx={{ color: '#d93025', fontSize: 13, mb: 1 }}>
                      {errorByIndex[index]}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {selectedFiles.map((file, fileIndex) => (
                      <Box
                        key={`${file.name}-${file.lastModified}-${fileIndex}`}
                        sx={{ position: 'relative' }}
                      >
                        <LocalPreview file={file} />
                        <Typography sx={{ color: '#5f6368', fontSize: 11 }}>
                          {formatBytes(file.size)}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label={t('jewellerDocument.remove')}
                          onClick={() => removeFile(index, fileIndex)}
                          disabled={isUploading}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: '#fff',
                            width: 22,
                            height: 22,
                            '&:hover': { bgcolor: '#f1f3f4' },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>

                  {uploaded && (
                    <Box sx={{ mt: selectedFiles.length ? 1.5 : 0 }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                        <CheckCircleOutlineIcon sx={{ color: '#188038', fontSize: 18 }} />
                        <Typography sx={{ fontWeight: 500, fontSize: 14, color: '#188038' }}>
                          {t('jewellerDocument.uploaded')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {(uploaded.filesPath || []).map((path) => (
                          <Box key={path} sx={{ position: 'relative' }}>
                            <RemotePreview
                              path={path}
                              onClosed={() => {
                                setUploadedByIndex((prev) => ({ ...prev, [index]: null }));
                                setFilesByIndex((prev) => ({ ...prev, [index]: [] }));
                                setErrorByIndex((prev) => ({ ...prev, [index]: '' }));
                              }}
                            />
                            <IconButton
                              size="small"
                              aria-label={t('jewellerDocument.remove')}
                              onClick={() => {
                                setUploadedByIndex((prev) => ({ ...prev, [index]: null }));
                                setFilesByIndex((prev) => ({ ...prev, [index]: [] }));
                                setErrorByIndex((prev) => ({ ...prev, [index]: '' }));
                              }}
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                zIndex: 2,
                                bgcolor: '#fff',
                                width: 22,
                                height: 22,
                                '&:hover': { bgcolor: '#f1f3f4' },
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
          </Box>
          {documents.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2.5, py: 2 }}>
              <Button
                variant="contained"
                disabled={typeof uploadingIndex === 'number'}
                onClick={() => uploadDocument(0)}
                sx={{
                  minWidth: 140,
                  height: 40,
                  borderRadius: '4px',
                  bgcolor: '#1a73e8',
                  color: '#fff',
                  textTransform: 'uppercase',
                  fontSize: 13,
                  fontWeight: 800,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#1765cc', boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: '#d2e3fc', color: '#fff' },
                }}
              >
                {typeof uploadingIndex === 'number' ? (
                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                ) : (
                  t('jewellerDocument.upload')
                )}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        open={forwardOpen}
        onClose={stayOnPage}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, textAlign: 'center', pb: 0.5 }}>
          {t('jewellerDocument.forwardTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#5f6368', textAlign: 'center', fontSize: 14 }}>
            {t('jewellerDocument.forwardMessage')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 2.5 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={goToDashboard}
            sx={{
              bgcolor: '#1a73e8',
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#1765cc', boxShadow: 'none' },
            }}
          >
            {t('common.dashboard')}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={stayOnPage}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {t('jewellerDocument.keepUpdated')}
          </Button>
          <Button
            fullWidth
            variant="text"
            color="error"
            onClick={logoutAfterUpload}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {t('common.logout')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadJewellerDocument;
